/**
 * Time Indicator Helpers
 *
 * Action-specific logic for calculating and formatting time indicators
 * displayed in the ActionCardHeader component.
 */

import dayjs from 'dayjs'
import { MP_VISIBILITY } from '@/constants'
import {
  formatTimeAgo,
  formatTimeRemaining,
  calculateExpiration,
  formatDays,
  isPast,
} from '@/utils/timeUtils'
import type { TaskEntry, ActionType } from '../types'

/**
 * Time indicator display data
 */
export interface TimeIndicatorData {
  /** Full text to display */
  text: string
  /** Part of text that should be bold */
  boldPart: string
  /** Color variant: error (red) or normal (black 60%) */
  color: 'error' | 'normal'
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Check if case is public (marketplace) vs private (invitation/referral)
 */
export const isPublicCase = (mpVisibility: string): boolean =>
  // FIXME: Currently it checks for a non-private rather than a direct
  // equality with MP_VISIBILITY.Public because the new Network status doesn't have
  // a business rule yet, so it is being treated as public for now.
  mpVisibility !== MP_VISIBILITY.Priority

/**
 * Check if user is the last attorney in the waterfall queue
 */
export const isLastInWaterfall = (
  waterfall: TaskEntry['waterfall']
): boolean => {
  if (!waterfall || waterfall.length === 0) return false

  const maxOrder = Math.max(...waterfall.map(w => w.waterfall_order))
  const currentItem = waterfall.find(
    w => w.current_item_order === w.waterfall_order
  )

  return currentItem?.waterfall_order === maxOrder
}

/**
 * Get the active case purchase (first one, or most recent active)
 */
export const getActiveCasePurchase = (
  casePurchases: TaskEntry['case_purchase']
): TaskEntry['case_purchase'][number] | null => {
  if (!casePurchases || casePurchases.length === 0) return null
  return casePurchases[0]
}

/**
 * Calculate days since last post-signed status update
 * Returns 0 if no history exists
 */
export const getDaysSinceLastUpdate = (
  history: TaskEntry['post_signed_status']
): number => {
  if (!history?.history || history.history.length === 0) return 0

  // Get most recent update (assuming array is sorted ascending by date, so newest is last)
  const latestUpdate = history.history[history.history.length - 1]
  const daysSince = dayjs().diff(dayjs(latestUpdate.created_ts), 'days')

  return daysSince
}

// ============================================================================
// ACTION-SPECIFIC HELPERS
// ============================================================================

/**
 * contact_client: Show days/hours remaining
 * Format: "XX days remaining" or "XX hours remaining" (bold: "XX days/hours")
 * Color: Normal
 */
const getContactClientIndicator = (
  task: TaskEntry
): TimeIndicatorData | null => {
  // TODO: Check if the commented logic is needed. Currently the endpoint is not
  // returning the days_value and due_date correctly for this type.

  // const days = task.urgency_metadata.days_value
  // const dueDate = task.urgency_metadata.due_date

  // // If due_date exists and days is 0, calculate hours remaining
  // if (dueDate && days === 0) {
  //   const hoursRemaining = dayjs(dueDate).diff(dayjs(), 'hours')
  //   if (hoursRemaining >= 0 && hoursRemaining < 24) {
  //     const hoursText = `${hoursRemaining} ${
  //       hoursRemaining === 1 ? 'hour' : 'hours'
  //     }`
  //     return {
  //       text: `${hoursText} remaining`,
  //       boldPart: hoursText,
  //       color: 'normal',
  //     }
  //   }
  // }

  // const daysText = formatDays(Math.abs(days))
  // return {
  //   text: `${daysText} remaining`,
  //   boldPart: daysText,
  //   color: 'normal',
  // }

  const casePurchase = task.case_purchase?.[0]
  if (!casePurchase) return null

  const daysLeft = casePurchase.days_left_to_sign
  if (daysLeft === undefined || daysLeft === null) return null

  // If expired (<= 0 days), show nothing
  if (daysLeft <= 0) return null

  const daysText = formatDays(daysLeft)
  return {
    text: `${daysText} remaining`,
    boldPart: daysText,
    color: 'normal',
  }
}

/**
 * in_progress: Show days remaining until escrow expiry
 * Format: "XX days remaining" (bold: "XX days")
 * Color: Normal
 */
const getInProgressIndicator = (task: TaskEntry): TimeIndicatorData | null => {
  const casePurchase = task.case_purchase?.[0]
  if (!casePurchase) return null

  const daysLeft = casePurchase.days_left_to_sign
  if (daysLeft === undefined || daysLeft === null) return null
  if (daysLeft <= 0) return null

  const daysText = formatDays(daysLeft)
  return {
    text: `${daysText} remaining`,
    boldPart: daysText,
    color: 'normal',
  }
}

/**
 * escrow_alert: Show days/hours remaining if ≤15 days, null if expired
 * Format: "XX days remaining" or "XX hours remaining" (bold: "XX days/hours")
 * Color: Error (red)
 */
const getEscrowAlertIndicator = (task: TaskEntry): TimeIndicatorData | null => {
  const casePurchase = task.case_purchase?.[0]
  if (!casePurchase) return null

  const daysLeft = casePurchase.days_left_to_sign
  if (daysLeft === undefined || daysLeft === null) return null

  // If expired (≤0 days), show nothing
  if (daysLeft <= 0) return null

  // Only show if ≤15 days
  if (daysLeft <= 15) {
    // If 0 days, calculate hours from expiration
    if (daysLeft === 0) {
      const expiration = calculateExpiration(
        casePurchase.active_ts,
        casePurchase.timed_access_limit,
        casePurchase.time_unit
      )

      const hoursRemaining = dayjs(expiration).diff(dayjs(), 'hours')
      if (hoursRemaining >= 0 && hoursRemaining < 24) {
        const hoursText = `${hoursRemaining} ${
          hoursRemaining === 1 ? 'hour' : 'hours'
        }`
        return {
          text: `${hoursText} remaining`,
          boldPart: hoursText,
          color: 'error',
        }
      }
    }

    const daysText = formatDays(daysLeft)
    return {
      text: `${daysText} remaining`,
      boldPart: daysText,
      color: 'error',
    }
  }

  return null
}

/**
 * respond_proposal: Show time remaining for private cases only
 *
 * Public marketplace proposals don't have expiration times.
 * Private proposals are shared directly with timed access and should show countdown.
 *
 * Uses case_purchase.status_ts, timed_access_limit, and time_unit to calculate expiration.
 *
 * Format: "X hrs remaining" or "X mins remaining" (bold: "X hrs" or "X mins")
 * Color: Error (red)
 */
const getRespondProposalIndicator = (
  task: TaskEntry
): TimeIndicatorData | null => {
  // Public marketplace proposals don't expire
  if (isPublicCase(task.mp_visibility)) return null

  // Private proposals have timed access - show expiration
  const casePurchase = getActiveCasePurchase(task.case_purchase)
  if (!casePurchase) return null

  // Calculate expiration from status_ts, timed_access_limit, and time_unit
  const expiration = calculateExpiration(
    casePurchase.status_ts,
    casePurchase.timed_access_limit,
    casePurchase.time_unit
  )

  // If already expired, don't show indicator
  if (isPast(expiration)) return null

  // Calculate hours remaining
  const hoursRemaining = dayjs(expiration).diff(dayjs(), 'hours')

  // Don't show if less than 0 hours (expired)
  if (hoursRemaining < 0) return null

  // If less than 1 hour, show minutes
  if (hoursRemaining < 1) {
    const minutesRemaining = dayjs(expiration).diff(dayjs(), 'minutes')
    // Show at least 1 minute if not expired
    const displayMinutes = Math.max(minutesRemaining, 1)
    const minsText = `${displayMinutes} ${
      displayMinutes === 1 ? 'min' : 'mins'
    }`

    return {
      text: `${minsText} remaining`,
      boldPart: minsText,
      color: 'error',
    }
  }

  const hoursText = `${hoursRemaining} ${hoursRemaining === 1 ? 'hr' : 'hrs'}`

  return {
    text: `${hoursText} remaining`,
    boldPart: hoursText,
    color: 'error',
  }
}

/**
 * accept_decline_invitation: Show expiration for private cases only
 * Format: "Expires in X time" (bold: "X time")
 * Color: Error (red)
 */
const getAcceptDeclineIndicator = (
  task: TaskEntry
): TimeIndicatorData | null => {
  // Public cases show nothing
  if (isPublicCase(task.mp_visibility)) return null

  // Private cases show expiration
  const casePurchase = getActiveCasePurchase(task.case_purchase)
  if (!casePurchase) return null

  const expiration = calculateExpiration(
    casePurchase.active_ts,
    casePurchase.timed_access_limit,
    casePurchase.time_unit
  )

  // If already expired, don't show
  if (isPast(expiration)) return null

  const timeText = formatTimeRemaining(expiration)

  return {
    text: `Expires in ${timeText}`,
    boldPart: timeText,
    color: 'error',
  }
}

/**
 * update_status_requested: Show days since request
 * Format: "Requested X days ago" (bold: "X days")
 * Color: Error (red)
 */
const getUpdateStatusRequestedIndicator = (
  task: TaskEntry
): TimeIndicatorData => {
  const requestDate =
    task.post_signed_status?.tasks?.[0]?.created_ts || task.created_ts
  const timeText = formatTimeAgo(requestDate)

  return {
    text: `Requested ${timeText} ago`,
    boldPart: timeText,
    color: 'error',
  }
}

/**
 * update_status_cron: Show time since last update
 * Format: "Last Update X days/months ago" (bold: "X days/months")
 * Color: 60-104 days = Normal, 105+ days = Error
 */
const getUpdateStatusCronIndicator = (task: TaskEntry): TimeIndicatorData => {
  const daysSince = getDaysSinceLastUpdate(task.post_signed_status)
  const timeText = formatTimeAgo(
    dayjs().subtract(daysSince, 'days').toISOString()
  )

  return {
    text: `Last Update ${timeText} ago`,
    boldPart: timeText,
    color: daysSince >= 105 ? 'error' : 'normal',
  }
}

/**
 * respond_counteroffer: Complex logic based on public/private, buyer/seller, waterfall position
 * Formats and colors vary by scenario
 */
const getRespondCounterofferIndicator = (
  task: TaskEntry,
  userId: string
): TimeIndicatorData | null => {
  // Public cases show nothing
  if (isPublicCase(task.mp_visibility)) return null

  const casePurchase = getActiveCasePurchase(task.case_purchase)
  if (!casePurchase) return null

  const expiration = calculateExpiration(
    casePurchase.active_ts,
    casePurchase.timed_access_limit,
    casePurchase.time_unit
  )

  // If expired, don't show
  if (isPast(expiration)) return null

  const timeText = formatTimeRemaining(expiration)
  const isUserSeller = task.attorney.user_id === userId

  // BUYER (Private): Show expiration
  if (!isUserSeller) {
    return {
      text: `Expires in ${timeText}`,
      boldPart: timeText,
      color: 'error',
    }
  }

  // SELLER (Private): Check waterfall position
  const isLast = isLastInWaterfall(task.waterfall)

  if (isLast) {
    // Last in queue: Posting to marketplace
    return {
      text: `Posting to Marketplace in ${timeText}`,
      boldPart: timeText,
      color: 'error',
    }
  }

  // Not last: Assign to next attorney
  return {
    text: `Assign to next attorney in ${timeText}`,
    boldPart: timeText,
    color: 'normal',
  }
}

/**
 * Master function to get time indicator data for any action type
 *
 * @param task - Task entry with all metadata
 * @param actionType - Type of action being displayed
 * @param userId - Current user's ID (for waterfall logic)
 * @returns Time indicator data or null if nothing should be displayed
 */
export const getTimeIndicatorData = (
  task: TaskEntry,
  actionType: ActionType,
  userId: string
): TimeIndicatorData | null => {
  switch (actionType) {
    case 'contact_client':
      return getContactClientIndicator(task)

    case 'in_progress':
      return getInProgressIndicator(task)

    case 'escrow_alert':
      return getEscrowAlertIndicator(task)

    case 'respond_proposal':
      return getRespondProposalIndicator(task)

    case 'accept_decline_invitation':
      return getAcceptDeclineIndicator(task)

    case 'update_status_requested':
      return getUpdateStatusRequestedIndicator(task)

    case 'update_status_cron':
      return getUpdateStatusCronIndicator(task)

    case 'respond_counteroffer':
      return getRespondCounterofferIndicator(task, userId)

    case 'data_room_request':
      return null

    // No deadline: the task has no due date, and escrow has not started.
    case 'share_contact_info':
    case 'awaiting_contact_info':
      return null

    default:
      return null
  }
}
